import {
  Grid,
  TextField,
  Typography,
  Button,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Modal,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import TaxIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import Swal from "sweetalert2";
import { getTaxTypes } from "../../api/taxTypes/getList";
import { getAddTax } from "../../api/taxes/addTax";
import { set } from "local-storage";

const AddOrRemoveTaxModal = ({
  addOrRemoveTaxModalOpen,
  setAddOrRemoveTaxModalOpen,
  selectedData,
  setSelectedData,
  handleSave,
  selectedMonth,
  selectedYear,
  types,
}) => {
  const [taxData, setTaxData] = useState([]);

  const saveControls = async (action) => {
    // Onay penceresi için mesajı action'a göre belirle
    const confirmMessage =
      action === "add"
        ? "Seçilen vergi türleri eklenecek. Emin misiniz?"
        : "Seçilen vergi türleri silinecek. Emin misiniz?";

    // Swal ile onay sorusu
    const result = await Swal.fire({
      title: "Emin misiniz?",
      text: confirmMessage,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Evet",
      cancelButtonText: "Hayır",
      reverseButtons: true,
    });

    // Kullanıcı "Evet" dediyse işlemi gerçekleştir
    if (result.isConfirmed) {
      // selectedData'yı güncelle
      const updatedSelectedData = selectedData.map((person) => {
        // Mevcut declarationsDetail'ı al (varsa), yoksa boş dizi
        const currentDeclarations = person.declarationDetails || [];

        if (action === "add") {
          // Ekleme işlemi: taxData'dan gelen ve mevcut declarationsDetail'da olmayan yeni vergi türlerini filtrele
          const newTaxesRaw = taxData.filter(
            (tax) =>
              !currentDeclarations.some((detail) => detail.taxTypeId === tax.id)
          );

          // Filtrelenmiş verileri istenen formata çevir
          const newTaxes = newTaxesRaw.map((tax) => ({
            title: tax.value, // taxData'daki value, title olarak ekleniyor
            status: false, // Status her zaman false
            taxTypeId: tax.id, // taxData'daki id, taxTypeId olarak ekleniyor
          }));

          // Yeni declarationsDetail: mevcut olanlar + yeni eklenenler
          const updatedDeclarationsDetail = [
            ...currentDeclarations,
            ...newTaxes,
          ];

          return {
            ...person,
            declarationDetails: updatedDeclarationsDetail,
            month: person.month || selectedMonth, // Şu anki ay (1-12 arası)
            year: person.year || selectedYear, // Şu anki yıl
          };
        } else if (action === "remove") {
          // Silme işlemi: taxData'daki id'lere göre declarationDetails'dan eşleşenleri çıkar
          const updatedDeclarationsDetail = currentDeclarations.filter(
            (detail) => !taxData.some((tax) => tax.id === detail.taxTypeId)
          );

          return {
            ...person,
            declarationDetails: updatedDeclarationsDetail,
            month: person.month || selectedMonth, // Şu anki ay (1-12 arası)
            year: person.year || selectedYear, // Şu anki yıl
          };
        }

        // Eğer action geçersizse, person'ı olduğu gibi döndür
        return person;
      });

      // Güncellenmiş veriyi handleSave'e gönder
      await handleSave(updatedSelectedData, setTaxData);
    }
  };

  const handleTaxTypeChange = (event, newValue) => {
    setTaxData(newValue); // taxData dizisini güncelle
  };

  const handleRemovePerson = (personId) => {
    console.log("selectedData", selectedData);

    setSelectedData((prevPeople) =>
      prevPeople.filter((person) => person.id !== personId)
    );
  };

  return (
    <Modal
      open={addOrRemoveTaxModalOpen}
      onClose={() => {
        setAddOrRemoveTaxModalOpen(false);
        setTaxData([]);
      }}
      aria-labelledby="add-tax-modal-title"
      aria-describedby="add-tax-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          padding: 4,
          borderRadius: 2,
          backgroundColor: "(255,255,255,0.1)",
          boxShadow: 3,
          maxWidth: "800px",
          margin: "auto",
        }}
      >
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                color: "#003366",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              Toplu Vergi Ekleme
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ pr: 2 }}>
            <Typography variant="h6" sx={{ color: "#003366", mb: 1 }}>
              <PersonIcon sx={{ mr: 1 }} /> Mükellefler
            </Typography>
            <Paper
              elevation={1}
              sx={{
                height: "400px",
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                p: 2,
              }}
            >
              <List>
                {selectedData?.map((person) => (
                  <ListItem
                    key={person.id}
                    disablePadding
                    sx={{
                      mb: 1,
                      bgcolor: "#ffffff",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "#e3f2fd" },
                      boxShadow: 1,
                    }}
                  >
                    <ListItemText
                      primary={person.title}
                      sx={{ textAlign: "center" }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="remove"
                        onClick={() => handleRemovePerson(person.id)}
                        sx={{ color: "#f44336" }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} sx={{ pl: 5, gap: 1 }}>
            <Autocomplete
              multiple
              id="tax-type-autocomplete"
              options={types} // Örn: [{value: "kdv1"}, {value: "kdv2"}, {value: "kdv5"}]
              getOptionLabel={(option) => option?.value} // Gösterilecek etiket 'value' alanından alınır
              value={taxData} // taxData'daki title'lara göre seçili olanları filtrele
              limitTags={3}
              size="small"
              onChange={handleTaxTypeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vergi Türleri"
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: "#757575" }}>
              <TaxIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Eklemek/Silmek istediğiniz vergileri seçiniz.
            </Typography>
            <Grid>
              <Button
                sx={{
                  mt: 2,
                  width: "100%",
                  height: "50px",
                  textTransform: "none",
                  bgcolor: "#4caf50",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "#45a049" },
                  "&:disabled": {
                    bgcolor: "#a5d6a7",
                    color: "#ffffff",
                  },
                }}
                variant="contained"
                onClick={() => saveControls("add")}
                startIcon={<SaveIcon />}
                disabled={selectedData.length === 0 || taxData.length === 0}
              >
                Kaydet
              </Button>{" "}
              <Button
                sx={{
                  mt: 2,
                  width: "100%",
                  height: "50px",
                  textTransform: "none",
                  bgcolor: "#990d0d",
                  color: "#ffffff",
                  "&:hover": { bgcolor: "#c42a2a" },
                  "&:disabled": {
                    bgcolor: "#cd38388b",
                    color: "#ffffff",
                  },
                }}
                variant="contained"
                onClick={() => saveControls("remove")}
                startIcon={<SaveIcon />}
                disabled={selectedData.length === 0 || taxData.length === 0}
              >
                Sil
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
};

export default AddOrRemoveTaxModal;
